/**
 * ENG-7927: opencode Live View transcript model + builder.
 *
 * opencode agents run headless `opencode serve`, so the tmux-pane Live View
 * (which scrapes an interactive Claude Code pane) shows only raw serve logs, not
 * the agent's conversation. The manager instead reads the structured session
 * messages off the serve's HTTP API and writes a REDACTED snapshot to
 * `~/.augmented/<code>/opencode-transcript.json`, which the admin API SSM-reads
 * for the webapp viewer (mirroring the pane-relay pattern; the serve password is
 * never written to disk).
 *
 * This file is the framework-agnostic, filesystem-free heart of that: it maps the
 * serve's `SessionMessage[]` into a compact, display-oriented transcript and
 * applies a caller-supplied redactor to every free-text field. It is PURE so the
 * redaction + shaping is unit-testable without a live server (the CLI injects the
 * real `redactForDiskLog` and a `Date.now()` timestamp; the API just re-serves
 * the parsed file).
 *
 * Deliberately DROPS raw tool INPUT (arbitrary JSON that can carry command
 * strings / secrets); it keeps only the tool name, live status, and opencode's
 * own redacted `title` summary - enough to see "bash `echo hello` (running)"
 * without exfiltrating call payloads.
 */

import type { SessionMessage, SessionMessagePart } from './opencode-client.js';

export type OpencodeTranscriptPartKind = 'text' | 'reasoning' | 'tool' | 'other';

export interface OpencodeTranscriptPart {
  kind: OpencodeTranscriptPartKind;
  /** Redacted text (for `text` / `reasoning` parts). */
  text?: string;
  /** Tool name (for `tool` parts), e.g. `bash`, `read`, `glob`. */
  tool?: string;
  /** Tool live status: `running` | `completed` | `error` (for `tool` parts). */
  status?: string;
  /** Redacted one-line tool summary opencode provides, e.g. `echo hello`. */
  title?: string;
}

export interface OpencodeTranscriptMessage {
  id?: string;
  /** `user` | `assistant` (opencode's message `type`). */
  role: string;
  createdAt?: number;
  completedAt?: number;
  /** Assistant turn finish reason (`stop`, `tool-calls`, …), null while running. */
  finish?: string;
  parts: OpencodeTranscriptPart[];
}

export interface OpencodeTranscript {
  version: 1;
  /** The session this snapshot is of, or null when the agent has no session yet. */
  sessionId: string | null;
  sessionTitle: string | null;
  /** Epoch ms the manager captured this snapshot (from the caller). */
  capturedAt: number;
  /** True when oldest messages were dropped to fit the size budget. */
  truncated: boolean;
  /** Oldest-first, so the viewer renders top-to-bottom like a chat. */
  messages: OpencodeTranscriptMessage[];
}

const IDENTITY = (s: string): string => s;

/** Default per-part text cap - a single huge tool dump can't blow the budget. */
const DEFAULT_MAX_PART_CHARS = 4_000;
/**
 * Default serialized-size budget. The admin API SSM-reads this file inline, and
 * SSM's StandardOutputContent tops out around 24 KB, so keep well under it -
 * a file over the cap would be byte-truncated on read and fail to JSON.parse.
 */
const DEFAULT_MAX_BYTES = 18_000;

/** Empty transcript for an agent with no session yet (still a valid file to serve). */
export function emptyOpencodeTranscript(capturedAt: number): OpencodeTranscript {
  return {
    version: 1,
    sessionId: null,
    sessionTitle: null,
    capturedAt,
    truncated: false,
    messages: [],
  };
}

export function buildOpencodeTranscript(input: {
  sessionId: string | null;
  sessionTitle?: string | null;
  messages: SessionMessage[];
  capturedAt: number;
  /** Applied to every free-text field (text/reasoning/tool title). Default: identity. */
  redact?: (s: string) => string;
  /** Keep only the newest N messages (the file stays bounded). */
  maxMessages?: number;
  /** Truncate each text/reasoning part to this many chars. Default 4000. */
  maxPartChars?: number;
  /** Drop oldest messages until the serialized JSON fits this many bytes. Default 18000. */
  maxBytes?: number;
}): OpencodeTranscript {
  const redact = input.redact ?? IDENTITY;
  const maxPartChars = input.maxPartChars ?? DEFAULT_MAX_PART_CHARS;
  const maxBytes = input.maxBytes ?? DEFAULT_MAX_BYTES;

  // opencode returns newest-first; render oldest-first, and pick by created time
  // rather than array order (order is not contractual).
  const sorted = [...input.messages].sort(
    (a, b) => (a.time?.created ?? 0) - (b.time?.created ?? 0),
  );
  const limited =
    typeof input.maxMessages === 'number' ? sorted.slice(-input.maxMessages) : sorted;
  let messages = limited.map((m) => buildMessage(m, redact, maxPartChars));

  // Drop from the FRONT (oldest) until the serialized transcript fits the byte
  // budget - the newest turns are what a live viewer cares about. Measure UTF-8
  // BYTES (not `.length`, which counts UTF-16 code units): the file is read back
  // over SSM's byte-capped inline output, so a transcript of multi-byte content
  // must be bounded by its real on-disk size.
  let truncated = false;
  const envelopeOverhead = 200; // version/session/capturedAt/flags scaffolding
  while (
    messages.length > 0 &&
    Buffer.byteLength(JSON.stringify(messages), 'utf8') + envelopeOverhead > maxBytes
  ) {
    messages = messages.slice(1);
    truncated = true;
  }

  return {
    version: 1,
    sessionId: input.sessionId,
    sessionTitle: input.sessionTitle ?? null,
    capturedAt: input.capturedAt,
    truncated,
    messages,
  };
}

function buildMessage(
  m: SessionMessage,
  redact: (s: string) => string,
  maxPartChars: number,
): OpencodeTranscriptMessage {
  return {
    id: m.id,
    role: typeof m.type === 'string' ? m.type : 'unknown',
    createdAt: m.time?.created,
    completedAt: m.time?.completed,
    finish: m.finish,
    parts: (m.content ?? []).map((p) => buildPart(p, redact, maxPartChars)),
  };
}

/** Redact, then cap a free-text field (appends an ellipsis marker when clipped). */
function clip(raw: string, redact: (s: string) => string, maxChars: number): string {
  const red = redact(raw);
  return red.length > maxChars ? `${red.slice(0, maxChars)}… [truncated]` : red;
}

function buildPart(
  p: SessionMessagePart,
  redact: (s: string) => string,
  maxPartChars: number,
): OpencodeTranscriptPart {
  if (p.type === 'text' || p.type === 'reasoning') {
    return { kind: p.type, text: typeof p.text === 'string' ? clip(p.text, redact, maxPartChars) : '' };
  }
  if (p.type === 'tool') {
    return {
      kind: 'tool',
      tool: typeof p.tool === 'string' ? p.tool : undefined,
      status: p.state?.status,
      // Redact the summary; DROP raw state.input entirely (may carry secrets).
      title: typeof p.state?.title === 'string' ? clip(p.state.title, redact, maxPartChars) : undefined,
    };
  }
  return { kind: 'other' };
}
